import { useState } from 'react'
import { apiErrorMessage } from '../api/client'

// useEditableField encapsulates the per-field edit lifecycle shared by the
// single-field cards in SettingsTab (FE-4): hold a local value seeded from
// the server value, track whether it's dirty, run an async save that maps a
// thrown ApiError to a message, and reset back to the seed on cancel.
//
// Field cards key themselves on the server value (`key={glow.name}`) so a
// PATCH that returns a normalized value remounts the card and re-seeds
// `initial` to the canonical value — this hook deliberately does NOT watch
// `initial` for changes, matching that remount-to-reseed contract.
export interface EditableField<T> {
  value: T
  setValue: (v: T) => void
  dirty: boolean
  error: string | null
  saving: boolean
  // save runs the provided async writer; on failure it stores a message
  // (apiErrorMessage with the given fallback). Returns true on success so
  // callers can chain a follow-up (e.g. close a panel).
  save: (writer: (value: T) => Promise<unknown>) => Promise<boolean>
  cancel: () => void
  // setError lets a caller surface a domain-specific message (e.g. mapping
  // a `subscription_required` code) before falling back to save's default.
  setError: (msg: string | null) => void
}

export function useEditableField<T>(
  initial: T,
  opts: { saving: boolean; saveFallback?: string },
): EditableField<T> {
  const [value, setValue] = useState<T>(initial)
  const [error, setError] = useState<string | null>(null)

  const dirty = value !== initial

  const save = async (writer: (value: T) => Promise<unknown>): Promise<boolean> => {
    setError(null)
    try {
      await writer(value)
      return true
    } catch (e) {
      setError(apiErrorMessage(e, opts.saveFallback ?? 'Could not save.'))
      return false
    }
  }

  const cancel = () => {
    setValue(initial)
    setError(null)
  }

  return {
    value,
    setValue,
    dirty,
    error,
    saving: opts.saving,
    save,
    cancel,
    setError,
  }
}
